import { GlobalDataContext } from '@/contexts/GlobalDataContext';
import { getBetSummaryReportState } from '@/modules/report/selector';
import { Summary } from '@/modules/report/slice/betSummary';
import { RwdTypography } from '@/modules/rwdGame/components/RwdTypography';
import { RootState } from '@/store/store';
import { AmountText } from '@/utils/formatter';
import {
    IconButton,
    IconButtonProps,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IcoPlayback } from '../../../svg';
import { DetailListDivider } from '../../ReportDivider';
import { RecordGameRoundText } from '../betRecordSummary/components/RecordGameRoundText';
import { BetContent } from './components/BetContent';
import GameResult from './components/gameResult';
import { PlaybackPanel } from './components/PlaybackPanel';
import { UnsuccessBetContent } from './components/UnsuccessBetContent';
import './index.scss';

type SummaryDetailProps = {
    summary: Summary;
};

export const SummaryDetail = (props: SummaryDetailProps) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const { externalWindowRef } = useContext(GlobalDataContext);

    const { successBetRecords, unsuccessBetRecords } = useSelector(
        (state: RootState) => getBetSummaryReportState(state)
    );

    const [onGoing, setOnGoing] = useState<boolean>(false);
    useEffect(() => {
        setOnGoing(props.summary.State == 0 || props.summary.WinSlot == 0);
    }, [props.summary.State]);

    const title_date_time =
        t('betlog.date') + ' / ' + t('betlog.time') + ' (UTC+8)';
    const title_game_round = t('betlog.game') + ' / ' + t('betlog.round');
    const title_gameID = t('betlog.game_id');
    const title_valid_bet = t('betlog.valid_bet_amount');
    const title_game_result = t('betlog.result');
    const title_bet_content = t('betlog.content');
    const title_unsuccessful_bet_content = t('betlog.unsuccessful');

    const [totalBetAmount, setTotalBetAmount] = useState(0);
    useEffect(() => {
        let betAmount = 0;
        if (successBetRecords) {
            for (const record of successBetRecords) {
                betAmount += record.BetAmount;
            }
        }

        setTotalBetAmount(betAmount);
    }, [successBetRecords]);

    const [openPlayback, setOpenPlayback] = useState(false);
    const onPlaybackClickHandler = () => {
        if (externalWindowRef.current) {
            externalWindowRef.current.close();
            externalWindowRef.current = null;
        }
        setOpenPlayback(true);
    };

    return (
        <>
            <Box className="summary-detail" ref={ref}>
                <List className="detail-list">
                    {/* Date / Time */}
                    <DetailListItem title={title_date_time} withDivider={true}>
                        <span>{`${props.summary.ReportDate.split(' ')[0]} / ${props.summary.ReportDate.split(' ')[1]}`}</span>
                    </DetailListItem>
                    {/* Game / Round */}
                    <DetailListItem title={title_game_round} withDivider={true}>
                        <RecordGameRoundText
                            summary={props.summary}
                            withSlash={true}
                        />
                    </DetailListItem>
                    {/* Game ID */}
                    <DetailListItem title={title_gameID} withDivider={true}>
                        <>
                            {props.summary.GameID}{' '}
                            <PlaybackButton onClick={onPlaybackClickHandler} />
                        </>
                    </DetailListItem>
                    {/* Valid Bet Amount */}
                    <DetailListItem title={title_valid_bet} withDivider={true}>
                        <>
                            {!onGoing && (
                                <AmountText
                                    amount={props.summary.RollingAmount / 100}
                                    textAlign={'end'}
                                    with_color={false}
                                    with_sign={false}
                                    zero_white_no_sign={true}
                                />
                            )}
                            {onGoing && (
                                <RwdTypography
                                    portraitFontSize={18}
                                    landscapeFontSize={36}
                                >
                                    -
                                </RwdTypography>
                            )}
                        </>
                    </DetailListItem>
                </List>
                <List className="detail-list below-part-background hide-scrollbar">
                    {/* Game Result */}
                    <GameResult
                        title={title_game_result}
                        summary={props.summary}
                        withDivider={true}
                        betRecords={successBetRecords}
                    />

                    <Box className="bet-content-title-container">
                        <DetailListItem title={title_bet_content} />
                    </Box>
                    {/* Bet Content */}
                    {successBetRecords && successBetRecords.length > 0 && (
                        <>
                            <BetContent
                                betRecords={successBetRecords}
                                gameType={props.summary.GameType}
                                totalBetAmount={totalBetAmount}
                                totalWinLoss={props.summary.ResultAmount}
                                onGoing={onGoing}
                                result={props.summary.Result}
                            />
                        </>
                    )}

                    {/* Bet Content (Unsuccessful) */}
                    {unsuccessBetRecords && unsuccessBetRecords.length > 0 && (
                        <>
                            <Box className="bet-content-title-container">
                                <DetailListItem
                                    title={title_unsuccessful_bet_content}
                                    titleColor="#979797"
                                />
                            </Box>

                            <UnsuccessBetContent
                                betRecords={unsuccessBetRecords}
                                gameType={props.summary.GameType}
                                result={props.summary.Result}
                            />
                        </>
                    )}

                    {/* Conditional Bottom Spacer */}
                    {(!successBetRecords ||
                        successBetRecords.length === 0 ||
                        !unsuccessBetRecords ||
                        unsuccessBetRecords.length === 0) && (
                        <div className="bottom-spacer" />
                    )}
                </List>
            </Box>
            {openPlayback && (
                <PlaybackPanel
                    open={openPlayback}
                    setIsOpen={setOpenPlayback}
                    summary={props.summary}
                />
            )}
        </>
    );
};

type DetailListItemProps = {
    title: string;
    titleColor?: string;
    onClick?: () => void;
    iconButton?: JSX.Element;
    setLangDirection?: boolean;
    children?: JSX.Element;
    withDivider?: boolean;
};

const DetailListItem = (props: DetailListItemProps) => {
    const {
        title,
        titleColor = '#BF9A5D',
        onClick,
        iconButton,
        setLangDirection,
        children,
        withDivider = false,
    } = props;
    const { i18n } = useTranslation();

    return (
        <>
            <ListItem className="detail-list-item" onClick={onClick}>
                <ListItemText>
                    <Stack
                        direction="row"
                        className="detail-list-item-text-root"
                    >
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography
                                sx={{ color: titleColor }}
                                className="detail-list-item-text-font"
                            >
                                {title}
                            </Typography>
                            {iconButton}
                        </Stack>

                        <Box
                            sx={{
                                direction: setLangDirection
                                    ? i18n.dir(i18n.language)
                                    : '',
                            }}
                            className="detail-list-item-text-font"
                        >
                            {children}
                        </Box>
                    </Stack>
                </ListItemText>
            </ListItem>
            {withDivider && <DetailListDivider />}
        </>
    );
};

const PlaybackButton = ({ onClick }: IconButtonProps) => (
    <IconButton
        id="btn_set-br_game-playback"
        disableRipple={true}
        onClick={onClick}
        className="play-back-button-icon"
    >
        <IcoPlayback width={'100%'} height={'100%'} />
    </IconButton>
);

export default SummaryDetail;
